import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { H3, StandardSection } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import PropTypes from 'prop-types';
import { pricingSelectors } from '../../state/pricing';
import { currencyLocaleFormatter } from '../../util/formatter';
import useSiteData from '../../hooks/useSiteData';
import { OrderPriceSummary } from './OrderPriceSummary';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';

export const TemplatePriceSummary = ({
  templateBillingDisplayType,
  currency,
  hideHeading,
}) => {
  const [templateBillingMessages] = useTranslations([
    'templateBillingMessages',
  ]);

  const templateItemIds = useSelector(state =>
    pricingSelectors.selectItemIdsOfTemplatePricingSummariesForDisplayType(
      state.pricing,
      templateBillingDisplayType
    )
  );
  const discountSources = useSelector(state =>
    pricingSelectors.selectTemplateDiscountSourcesByDisplayType(
      state.pricing,
      templateBillingDisplayType
    )
  );
  const itemPricing = useSelector(state =>
    pricingSelectors.selectTemplatePricingSummaries(state.pricing)
  );

  const isLoaded = useSelector(state =>
    pricingSelectors.selectPricingIsValid(state.pricing)
  );
  const fractionDigits = useSelector(state =>
    pricingSelectors.selectFractionDigits(state.pricing)
  );

  const { uiLocalization, locale } = useSiteData();

  const { clusivitySeparatedTemplatePrices, taxDisplayContext } =
    useContext(TaxDisplayContext);

  const currencyLocale = currencyLocaleFormatter(
    uiLocalization && uiLocalization.locale
  );

  const validClusivitySeparatedTemplatePrices = [];
  templateItemIds.forEach(id => {
    if (clusivitySeparatedTemplatePrices[id]) {
      validClusivitySeparatedTemplatePrices.push(
        clusivitySeparatedTemplatePrices[id]
      );
    }
  });

  // subtotal by key 'product'
  const subtotals = [{ key: 'product', level: 'order' }];
  const aggregatedTemplateSummaryPrice = aggregateTaxAwarePrices(
    validClusivitySeparatedTemplatePrices.flat(),
    'order',
    'default',
    subtotals,
    taxDisplayContext
  );
  const aggregatedProductPrice =
    aggregatedTemplateSummaryPrice.subtotals.get('product');

  return (
    <StandardSection mb={0}>
      <H3 marginTop={1} fontSize={2} hidden={hideHeading}>
        {
          templateBillingMessages[
            `templateSummaryLabel:${templateBillingDisplayType}`
          ]
        }
      </H3>

      <OrderPriceSummary
        key={`${templateBillingDisplayType}`}
        productSubtotalLabel={
          templateBillingMessages[
            `templateSummarySubtotal:${templateBillingDisplayType}`
          ]
        }
        orderTotalLabel={
          templateBillingMessages[
            `templatePriceLabel:${templateBillingDisplayType}`
          ]
        }
        fractionDigits={fractionDigits}
        currency={currency}
        currencyLocale={currencyLocale}
        isLoaded={isLoaded}
        unit={
          templateBillingMessages[
            `templatePriceUnit:${templateBillingDisplayType}`
          ]
        }
        orderPrice={aggregatedTemplateSummaryPrice}
        productPrice={aggregatedProductPrice}
        siteLocale={locale}
        discountSources={discountSources}
        pricingData={itemPricing}
      />
    </StandardSection>
  );
};

TemplatePriceSummary.propTypes = {
  templateBillingDisplayType: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  hideHeading: PropTypes.bool,
};
